import { useState, useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  getNotificationData,
  putNotificationData,
} from "@services/API/Requests/NotificationRequests";
import { NOTIFICATION } from "@types/notification";
import { TranslateErrorMessage } from "@services/ErrorHandling";
import { useSelector } from "react-redux";
import messages from "@constants/Messages";
import { error_message } from "@constants/Errors";
import { NOTIFICATION_PATHS } from "@constants/NavigationPaths";
import { validateDates } from "@pages/Notification/common/utilities/ValidateDates";

export const useNotificationPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { id } = useParams();

  const startToast = () => toast.success(messages.notification.restart);
  const stopToast = () => toast.success(messages.notification.stop);

  const startModalTitle: string = "再配信注意";
  const startModalBody: string = "お知らせを再配信します。よろしいですか？";
  const startModalConfirm: string = "再配信する";

  const pauseModalTitle: string = "停止注意";
  const pauseModalBody: string = "お知らせを停止します。よろしいですか？";
  const pauseModalConfirm: string = "停止する";

  const editSuccess: string = "お知らせ情報を更新しました。";

  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>("");
  const [notification, setNotification] = useState<NOTIFICATION | undefined>();
  const [pauseModalOpen, setPauseModalOpen] = useState<boolean>(false);
  const [startModalOpen, setStartModalOpen] = useState<boolean>(false);
  const [isEditing, setIsEditing] = useState<boolean>(true);

  const errorToast = (response: any) => toast.error(`${response}`);
  const successToast = () => toast.success(editSuccess);

  const isAdminLoggedIn: boolean = useSelector((state) => state.auth.isAdmin);

  const viewOnly = location.state?.viewOnly ?? !isAdminLoggedIn;

  useEffect(() => {
    getNotificationData(setNotification, setLoading, setError, id);
  }, []);

  const handleStopClick = () => {
    setPauseModalOpen(true);
  };

  const handleStopCloseModal = () => {
    setPauseModalOpen(false);
  };

  const handlePutNotificationData = async (successMessage: () => void) => {
    putNotificationData(notification, setLoading, setError, notification?.id)
      .then((response: any) => {
        if (response.status == 200) {
          successMessage();
        } else {
          const errorMessage = TranslateErrorMessage(response.message);
          console.error(errorMessage);
          setError(errorMessage);
          errorToast(errorMessage);
        }
      })
      .catch(() => {
        const errorMessage = TranslateErrorMessage(response.message);
        console.error(errorMessage);
        setError(errorMessage);
        errorToast(errorMessage);
      });
  };
  const handleStopConfirm = () => {
    setPauseModalOpen(false);
    if (notification) {
      notification.status = !notification?.status;
      handlePutNotificationData(stopToast);
    } else {
      console.error(error_message.notification.is_null);
    }
  };

  const handleCloseStartModal = () => {
    setStartModalOpen(false);
  };

  const handleStartClick = () => {
    setStartModalOpen(true);
  };

  const handleStartConfirm = () => {
    setStartModalOpen(false);
    if (notification) {
      notification.status = !notification.status;
      handlePutNotificationData(startToast);
    } else {
      console.error(error_message.notification.is_null);
    }
  };

  const toggleEditMode = () => {
    setIsEditing((prev) => !prev);
  };

  const handleCancelButtonClick = () => {
    if (isEditing) {
      navigate(NOTIFICATION_PATHS.main);
    } else {
      toggleEditMode();
    }
  };

  const handleUpdateButtonClick = () => {
    if (!isEditing) {
      const validDates: boolean = validateDates(errorToast, notification);

      if (!validDates) {
        return;
      }

      handlePutNotificationData(successToast);
      setTimeout(() => {
        navigate(NOTIFICATION_PATHS.main);
      }, 3000);
    } else {
      toggleEditMode();
    }
  };

  return {
    notification,
    viewOnly,
    loading,
    isAdminLoggedIn,
    isEditing,
    startModalTitle,
    startModalBody,
    startModalConfirm,
    pauseModalTitle,
    pauseModalOpen,
    pauseModalBody,
    pauseModalConfirm,
    startModalOpen,
    setNotification,
    handleStartClick,
    handleStopClick,
    handleCancelButtonClick,
    handleUpdateButtonClick,
    handleStopCloseModal,
    handleStopConfirm,
    handleCloseStartModal,
    handleStartConfirm,
  };
};
